import { Session, User } from '@supabase/supabase-js';
import {
  PropsWithChildren,
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useSupabase } from './supabase-provider';

interface UserContextType {
  user: User | null;
  session: Session | null;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

interface UserProviderProps extends PropsWithChildren {
  initialUser?: User | null;
  initialSession?: Session | null;
}

export function UserProvider({
  children,
  initialUser = null,
  initialSession = null,
}: UserProviderProps) {
  const supabase = useSupabase();
  const [user, setUser] = useState<User | null>(initialUser);
  const [session, setSession] = useState<Session | null>(initialSession);

  // Listen to supabase auth changes
  useEffect(() => {
    const authState = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT') {
        setUser(null);
        setSession(null);
      } else {
        setSession(session);
        setUser(session?.user ?? null);
      }
    });

    return () => authState.data.subscription.unsubscribe();
  }, [supabase.auth]);

  return (
    <UserContext.Provider value={{ user, session }}>
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  const context = useContext(UserContext);
  if (!context) throw new Error('useUser must be used within an UserProvider');
  return context;
}
